import React, { useContext, useState, useRef, useEffect, useMemo } from "react";
import {
  Album,
  ShortCutType,
  hierarchyEntitiesType,
  SourceEntityToMoveType
} from "./types";
import { PhotoManagerContext } from "./index";
import {
  useEventData,
  useModalStates,
  getUrlParams
} from "./BreadcrumbsUtilities";
import ShareSocialPopup from "../../ShareSocialPopup";
import ConfirmationPopup from "../../EventsDashboard/ConfirmationPopup";
import PublishingOptions from "../../PublishingOptions";
import NewAlbumModal from "./NewAlbumModal";
import BreadcrumbEventActions from "./BreadcrumbEventActions";
import { usePopupClickOutside } from "../../../hooks/usePopupClickOutside";
import DeleteShortcut from "../components/FolderFeatures/DeleteShortcut";
import MoveToFolderPopup from "../components/MoveToFolderPopup";
import {
  decodeEntityPath,
  getEntityInfoFromPath
} from "./FolderFeatures/EncryptionOfFolder";
import PublishModeIcon from "./PublishModeIcon";
import { fetchEventList, updateEventAPI } from "../API/api";
import { useEditEntityName } from "./useEditEntityName";

const GreenArrow = require("../styles/images/GreenArrow.svg");
const arrowDownIcon = require("../styles/images/white-down-arrow.svg");
const shortcutIcon = require("../styles/images/shortcut-icon.svg");
const folderIcon = require("../styles/images/folder-icon.svg");
const editIcon = require("../styles/images/edit.svg");
const submitIcon = require("../../ManagePrepaidCustomers/components/images/submit.svg");
const cancelIcon = require("../../ManagePrepaidCustomers/components/images/cancel.svg");

interface AlbumListBreadcrumbProps {}

const AlbumListBreadcrumb: React.FC<AlbumListBreadcrumbProps> = ({}) => {
  const [showOrganizeOptns, setShowOrganizeOptns] = useState<boolean>(false);
  const [showHiddenLinks, setShowHiddenLinks] = useState<boolean>(false);
  const [showDeleteShortcutModal, setShowDeleteShortcutModal] =
    useState<boolean>(false);
  const [showMovePopup, setShowMovePopup] = useState<boolean>(false);
  const [srcEntity, setSrcEntity] = useState<SourceEntityToMoveType | null>(
    null
  );
  const [referenceId, setReferenceId] = useState<string | null>(null);

  const {
    isInEditMode,
    entityNameDraft,
    toggleEditMode,
    updateDraftName,
    handleKeyDownAction,
    submitNameChange,
    hasInvalidatedValues
  } = useEditEntityName();

  const [displayName, setDisplayName] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const popUpRef = useRef<HTMLDivElement>(null);
  usePopupClickOutside(popUpRef, () => {
    setShowHiddenLinks(false);
    setShowOrganizeOptns(false);
  });

  const context = useContext(PhotoManagerContext);

  const { entityType, entityId } = useMemo(() => {
    // Try context first (preferred), then fall back to URL for fresh data
    const encodedPath =
      context?.encodedString ||
      new URL(window.location.href).searchParams.get("p");

    // Also get URL directly to ensure we have the latest data after deletions
    const urlEncodedPath = new URL(window.location.href).searchParams.get("p");

    // Use URL path if context seems stale (different from URL)
    const finalEncodedPath =
      context?.encodedString !== urlEncodedPath && urlEncodedPath
        ? urlEncodedPath
        : encodedPath;

    if (!finalEncodedPath) return { entityType: null, entityId: null };

    const decodedPath = decodeEntityPath(finalEncodedPath);
    if (!decodedPath) return { entityType: null, entityId: null };

    const { type, id } = getEntityInfoFromPath(decodedPath);

    let resolvedType = type;
    let resolvedId = id;

    if (!resolvedType) {
      const lastSegment = decodedPath.split("/").pop();
      if (lastSegment?.startsWith("r:")) {
        resolvedType = "reference";
        resolvedId = parseInt(lastSegment.split(":")[1], 10);
      } else if (lastSegment?.startsWith("g:")) {
        resolvedType = "gallery";
        resolvedId = parseInt(lastSegment.split(":")[1], 10);
      } else if (lastSegment?.startsWith("a:")) {
        resolvedType = "album";
        resolvedId = parseInt(lastSegment.split(":")[1], 10);
      }
    }

    const finalType =
      resolvedType === "reference"
        ? "LibraryFolderReference"
        : resolvedType === "album"
        ? "Album"
        : "Gallery";

    return { entityType: finalType, entityId: resolvedId };
  }, [context?.encodedString, context?.selectedEvent]);

  useEffect(() => {
    const encodedPath =
      context?.encodedString ||
      new URL(window.location.href).searchParams.get("p");
    if (!encodedPath) return;

    const decodedPath = decodeEntityPath(encodedPath);
    if (!decodedPath) return;

    const { type } = getEntityInfoFromPath(decodedPath);
    let resolvedType = type;
    if (!resolvedType) {
      const lastSegment = decodedPath.split("/").pop();
      if (lastSegment?.startsWith("r:")) {
        resolvedType = "reference";
      } else if (lastSegment?.startsWith("g:")) {
        resolvedType = "gallery";
      }
    }

    // Set referenceId when it's a reference type
    if (resolvedType === "reference") {
      setReferenceId(entityId);
    } else {
      setReferenceId(null);
    }
  }, [context?.encodedString, entityId]);

  const { selectedEvent, loadingEvent, fetchEventData } = useEventData(
    entityType,
    entityId
  );

  const {
    activeShareModalEventId,
    setActiveShareModalEventId,
    markUploadPopupId,
    setMarkUploadPopupId,
    privacyPopupId,
    setPrivacyPopupId,
    showNewAlbumModal,
    setShowNewAlbumModal
  } = useModalStates();

  const handleBreadcrumbClick = () => {
    context?.navigateToRoot?.();
    const target = context?.invitedEventsMode
      ? "/library?invited_events=true"
      : "/library";
    window.history.pushState({}, "", target);
    context?.setSelectedView(null);
  };
  const isDeeplyNested = selectedEvent && selectedEvent?.hierarchy.length > 2;
  const hiddenLinks = selectedEvent && selectedEvent?.hierarchy.slice(0, -2);
  const shownLinks =
    selectedEvent && selectedEvent?.hierarchy.slice(-2).slice(0, -1);

  const handleInlineRenameSubmit = async () => {
    if (selectedEvent) {
      if (hasInvalidatedValues(true, entityNameDraft)) {
        return;
      }

      setIsSubmitting(true);

      try {
        await updateEventAPI(selectedEvent.id, { name: entityNameDraft });

        setDisplayName(entityNameDraft);
        toggleEditMode(false, "");

        await fetchEventData();

        if (selectedEvent.libraryFolderId) {
          await context?.reloadEventData(
            "nested",
            selectedEvent.libraryFolderId,
            "LibraryFolder"
          );
        } else {
          await context?.reloadEventData("root");
        }
      } catch (error) {
        console.error("Error updating event name:", error);
        //@ts-ignore
        toastr.error("Failed to update event name. Please try again");
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const onDeleteShortCut = async (shortCut: ShortCutType) => {
    setShowDeleteShortcutModal(false);
    // Refresh entire sidebar to ensure shortcut is removed from all locations and events list is updated
    await context?.refreshEntireSideBar();
  };

  const onMovingSuccess = (val: {
    shouldReloadTreeView: boolean;
    src: any;
    destination: any;
  }) => {
    setShowMovePopup(false);
    // Redirect to parent folder after moving
    if (srcEntity?.parentFolderId) {
      context?.handleBreadcrumbClick(srcEntity.parentFolderId, "LibraryFolder");
    } else {
      window.location.href = "/library";
    }

    // Update sidebar - always reload source folder data (where event was moved from)
    if (srcEntity?.parentFolderId) {
      // Event was moved from a nested folder
      context?.reloadEventData(
        "nested",
        srcEntity.parentFolderId,
        "LibraryFolder"
      );
    } else {
      // Event was moved from root - reload root events
      context?.reloadEventData("root");
    }

    // Update destination folder in sidebar (conditionally if expanded)
    if (val.destination?.entityId) {
      const isExpanded = context?.isExpandedFolder?.(val.destination.entityId);
      if (isExpanded) {
        context?.reloadEventData(
          "nested",
          val.destination.entityId,
          "LibraryFolder"
        );
      }
    }

    if (val.shouldReloadTreeView) {
      context?.reloadEventData("root");
    }
  };

  const renderParentLinks = () => {
    if (isDeeplyNested) {
      return (
        <>
          <div className="deeply-nested-view">
            <button
              type="button"
              className="green-text geo-text-btn"
              onClick={() => setShowHiddenLinks(!showHiddenLinks)}
            >
              ...
            </button>
            {showHiddenLinks && (
              <div className="hidden-links-card">
                {hiddenLinks?.map((link: hierarchyEntitiesType) => {
                  return (
                    <div key={link.id}>
                      <img />
                      <button
                        type="button"
                        className="hidden-links geo-text-btn text-limit black-text"
                        onClick={() =>
                          context?.handleBreadcrumbClick(
                            link.id,
                            link.entityType,
                            link.entityType === "LibraryFolderReference"
                              ? "folder"
                              : undefined
                          )
                        }
                      >
                        {link.entityType === "LibraryFolderReference" ? (
                          <img src={shortcutIcon} />
                        ) : (
                          <img src={folderIcon} />
                        )}
                        {link.name}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <span className="grey-text bc-links-divider"> / </span>
          {shownLinks?.map((parent: hierarchyEntitiesType) => {
            return (
              <React.Fragment key={parent.id}>
                <button
                  type="button"
                  className="green-text geo-text-btn text-limit-w"
                  onClick={() =>
                    context?.handleBreadcrumbClick(
                      parent.id,
                      parent.entityType,
                      parent.entityType === "LibraryFolderReference"
                        ? "folder"
                        : undefined
                    )
                  }
                >
                  {parent.entityType === "LibraryFolderReference" && (
                    <img src={shortcutIcon} />
                  )}
                  {parent.name}
                </button>
                <span className="grey-text bc-links-divider"> / </span>
              </React.Fragment>
            );
          })}
        </>
      );
    }
    return (
      <>
        {selectedEvent?.hierarchy
          .slice(0, -1)
          .map((parent: hierarchyEntitiesType) => {
            return (
              <React.Fragment key={parent.id}>
                <button
                  type="button"
                  className="green-text geo-text-btn text-limit-w"
                  aria-label="Back to Library"
                  onClick={() =>
                    context?.handleBreadcrumbClick(
                      parent.id,
                      parent.entityType,
                      parent.entityType === "LibraryFolderReference"
                        ? "folder"
                        : undefined
                    )
                  }
                >
                  {parent.entityType === "LibraryFolderReference" && (
                    <img src={shortcutIcon} />
                  )}
                  {parent.name}
                </button>
                <span className="grey-text bc-links-divider"> / </span>
              </React.Fragment>
            );
          })}
      </>
    );
  };

  const OrganizeOptns = () => {
    if (selectedEvent?.isReference) {
      return (
        <div
          className="dropdown-menu organize-optns"
          style={{ display: "block", position: "absolute" }}
          ref={popUpRef}
        >
          <button
            type="button"
            className="organize-btn"
            onClick={() => setShowDeleteShortcutModal(true)}
          >
            <span className="optn">Delete shortcut</span>
          </button>
        </div>
      );
    }
    return (
      <div
        className="dropdown-menu dropdown-menu-right organize-optns"
        style={{ display: "block", position: "absolute" }}
        ref={popUpRef}
      >
        {selectedEvent?.canRename && (
          <button
            type="button"
            className="organize-btn"
            onClick={() => {
              if (selectedEvent) {
                context?.setShowRenameEventModal(selectedEvent);
                setShowOrganizeOptns(false);
              }
            }}
            style={{ paddingBottom: 14 }}
          >
            <span className="optn">Rename</span>
          </button>
        )}
        {selectedEvent?.canRename && <hr />}
        {selectedEvent?.canMove && (
          <button
            type="button"
            className="organize-btn"
            onClick={() => {
              if (selectedEvent) {
                const srcEntity: SourceEntityToMoveType = {
                  id: selectedEvent.id,
                  name: selectedEvent.name,
                  type: "Gallery",
                  parentFolderId: selectedEvent.libraryFolderId
                };
                setSrcEntity(srcEntity);
                setShowMovePopup(true);
              }
            }}
          >
            <span className="optn">Move this event</span>
          </button>
        )}
        {selectedEvent?.canAddShortcut && (
          <button
            type="button"
            className="organize-btn"
            onClick={() => {
              if (selectedEvent)
                context?.toggleFolderFeatureModal(
                  true,
                  selectedEvent.id,
                  selectedEvent.name,
                  "Event",
                  selectedEvent.parentFolderId || null
                );
            }}
          >
            <span className="optn">Add shortcut</span>
            <span className="optn-info">Create a shortcut of this event</span>
          </button>
        )}
        {selectedEvent?.canManageAccess && (
          <button
            type="button"
            className="organize-btn"
            onClick={() => {
              if (selectedEvent) {
                context?.toggleManagePermissionsModal(
                  true,
                  "Gallery",
                  selectedEvent.id,
                  selectedEvent.name
                );
                setShowOrganizeOptns(false);
              }
            }}
          >
            <span className="optn">Manage access</span>
            <span className="optn-info">Control who can view and edit</span>
          </button>
        )}
        {selectedEvent?.canDelete && <hr />}
        {selectedEvent?.canDelete && (
          <button
            type="button"
            className="organize-btn"
            onClick={() => {
              if (selectedEvent) {
                context?.setShowDeleteEventModal(selectedEvent);
                setShowOrganizeOptns(false);
              }
            }}
            style={{ paddingTop: 14 }}
          >
            <span className="optn">Delete</span>
          </button>
        )}
      </div>
    );
  };

  return (
    <>
      <div className="ws-breadcrumbs ws-action-and-breadcrumbs">
        <div className="album-breadcrumb-left-content ws-txtarea-width">
          <p className="ws-head-txt">
            <button
              type="button"
              className="green-text geo-text-btn"
              aria-label={
                context?.invitedEventsMode
                  ? "Back to Invited Events"
                  : "Back to Library"
              }
              onClick={handleBreadcrumbClick}
            >
              <img src={GreenArrow} alt="Back" style={{ marginRight: 5 }} />
              {context?.invitedEventsMode ? "Invited Events" : "Library"}
            </button>
            <span className="grey-text bc-links-divider"> / </span>
            {!context?.invitedEventsMode && renderParentLinks()}
            {loadingEvent ? (
              <span className="loading-spinner">
                <span className="fa fa-spinner fa-spin"></span>
              </span>
            ) : (
              <span
                className="grey-text grey-text-limit-w"
                data-toggle="tooltip"
                data-placement="bottom"
                data-original-title={selectedEvent?.name}
              >
                {selectedEvent?.isReference && <img src={shortcutIcon} />}
                <span className="text-truncate">{selectedEvent?.name}</span>
              </span>
            )}
          </p>

          {loadingEvent ? (
            <div className="loading-spinner event-name-spinner">
              <span className="fa fa-spinner fa-spin"></span>
            </div>
          ) : isInEditMode ? (
            <div
              className="ws-edit-album-name ws-edit-section-padding black-text"
              onClick={e => e.stopPropagation()}
            >
              <textarea
                defaultValue={selectedEvent?.name}
                onChange={updateDraftName}
                onKeyDown={(e: React.KeyboardEvent<HTMLTextAreaElement>) =>
                  handleKeyDownAction(e, handleInlineRenameSubmit)
                }
                onClick={e => e.stopPropagation()}
                autoFocus
                onFocus={e => {
                  const val = e.target.value;
                  e.target.value = "";
                  e.target.value = val;
                }}
              />
              <div className="ws-edit-album-actions ws-breadrumb-edit-actions">
                {isSubmitting ? (
                  <span
                    className="fa fa-spinner fa-spin"
                    style={{ marginRight: 5 }}
                  ></span>
                ) : (
                  <>
                    <img
                      className="actionIcon"
                      src={submitIcon}
                      onClick={e =>
                        submitNameChange(e, handleInlineRenameSubmit)
                      }
                      style={{ cursor: "pointer", opacity: 1 }}
                    />
                    <img
                      className="actionIcon"
                      style={{ marginLeft: 5, cursor: "pointer", opacity: 1 }}
                      src={cancelIcon}
                      onClick={e =>
                        toggleEditMode(false, selectedEvent?.name || "", e)
                      }
                    />
                  </>
                )}
              </div>
            </div>
          ) : (
            <span className="black-text event-name-text ws-folder-name-div">
              {selectedEvent?.isReference && (
                <img className="lib-shortcut" src={shortcutIcon} />
              )}
              <span className="ws-edit-name-h3">
                {displayName || selectedEvent?.name}
              </span>
              {context?.invitedEventsMode && (
                <span className="invited-event-badge">INVITED</span>
              )}
              {!selectedEvent?.isReference && selectedEvent?.canRename && (
                <span
                  className="ws-edit-name-icon ws-breadcrumb-edit-icon"
                  data-toggle="tooltip"
                  data-placement="top"
                  data-container="body"
                  title="Edit name"
                  id={`edit-icon-${selectedEvent?.id}`}
                  onMouseEnter={e =>
                    context?.showActionTooltip(
                      selectedEvent?.id || null,
                      "Edit Name",
                      "edit"
                    )
                  }
                  onClick={e =>
                    toggleEditMode(true, selectedEvent?.name || "", e)
                  }
                >
                  <img src={editIcon} alt="Edit event name" />
                </span>
              )}
              {selectedEvent && (
                <PublishModeIcon
                  event={selectedEvent}
                  // @ts-ignore
                  showActionTooltip={context?.showActionTooltip}
                />
              )}
            </span>
          )}
        </div>
        {loadingEvent && !context?.isMobile ? (
          <div className="loading-spinner">
            <span className="fa fa-spinner fa-spin"></span>
          </div>
        ) : (
          selectedEvent &&
          selectedEvent.browsePhotosURL && (
            <div className="ws-album-actions-main">
              <BreadcrumbEventActions
                selectedEvent={selectedEvent}
                setActiveShareModalEventId={setActiveShareModalEventId}
                setMarkUploadPopupId={setMarkUploadPopupId}
                setPrivacyPopupId={setPrivacyPopupId}
                context={context}
              />

              <div className="ws-mobile-sub-header">
                <div className="ws-mobile-header">
                  {selectedEvent.uploadCompleteStatus === true ? (
                    <div className="event-up-done ws-actions-right-border">
                      <span className="event-up-check pop-check"></span>
                      <span className="event-up-donetext">Upload complete</span>
                    </div>
                  ) : (
                    selectedEvent.markUploadCompleteURL !== null &&
                    selectedEvent.uploadCompleteStatus !== null && (
                      <div className="ws-leads-count ws-actions-right-border">
                        <span
                          className="ws-event-up-link"
                          onClick={e => {
                            e.stopPropagation();
                            setMarkUploadPopupId(selectedEvent.id);
                          }}
                        >
                          Mark upload complete
                        </span>
                        {selectedEvent.waitingCustomerLeadsCount > 0 && (
                          <div className="ws-event-wait">
                            {selectedEvent.waitingCustomerLeadsCount === 1 ? (
                              <span>
                                <b>1 person</b> is waiting
                              </span>
                            ) : (
                              <span>
                                <b>{selectedEvent.waitingCustomerLeadsCount} people</b>{" "}
                                are waiting
                              </span>
                            )}{" "}
                          </div>
                        )}
                      </div>
                    )
                  )}
                </div>
                {(selectedEvent?.canRename ||
                  selectedEvent?.canMove ||
                  selectedEvent?.canAddShortcut ||
                  selectedEvent?.canManageAccess ||
                  selectedEvent?.canDelete ||
                  selectedEvent?.isReference) && (
                    <button
                      className="geo-btn geo-btn--small geo-btn--secondary geo-btn--caps ws-organize-btn"
                      onClick={e => {
                        setShowOrganizeOptns(!showOrganizeOptns);
                      }}
                    >
                      Organise
                      <img
                        id="ws-arrow-down-icon"
                        src={arrowDownIcon}
                        alt="Uploader-options"
                      />
                      {showOrganizeOptns && <OrganizeOptns />}
                    </button>
                  )}
                {selectedEvent?.canCreateAlbums && (
                  <button
                    className="geo-btn geo-btn--small geo-btn--secondary geo-btn--caps ws-upld-btn"
                    onClick={e => setShowNewAlbumModal(true)}
                  >
                    Create an Album
                  </button>
                )}
              </div>
            </div>
          )
        )}
      </div>

      {showNewAlbumModal && selectedEvent && context && (
        <NewAlbumModal
          eventId={selectedEvent.id}
          onClose={() => setShowNewAlbumModal(false)}
          onCreateAlbumSuccess={(newAlbum: Album) => {
            context.onCreateAlbumSuccess(newAlbum);
          }}
        />
      )}

      {activeShareModalEventId !== null && selectedEvent && (
        <div className="event-share-modal">
          <ShareSocialPopup
            galleryURL={selectedEvent?.browsePhotosURL}
            hideShareGalleryModal={() => setActiveShareModalEventId(null)}
            text="Share with participants or just show off ;)"
          />
        </div>
      )}

      {markUploadPopupId && selectedEvent && context && (
        <ConfirmationPopup
          closePopup={() => setMarkUploadPopupId(null)}
          popupId={selectedEvent?.markUploadCompleteURL}
          eventId={selectedEvent.id}
          onSuccess={() => {
            fetchEventData();
            context?.setRootEventsView();
            setMarkUploadPopupId(null);
          }}
          loader={context.confirmationLoader}
        />
      )}

      {privacyPopupId && selectedEvent && context && (
        <PublishingOptions
          closePopup={() => setPrivacyPopupId(null)}
          eventData={{
            ...selectedEvent,
            url: selectedEvent?.browsePhotosURL || ""
          }}
          updateEventPrivacy={() => {
            fetchEventData();
            context?.setRootEventsView();
            setPrivacyPopupId(null);
          }}
          schoolUser={context.schoolUser}
          publicSharingDisabled={context.publicSharingDisabled}
          eventPasswordsDisabled={context.eventPasswordsDisabled}
        />
      )}
      {showDeleteShortcutModal && referenceId && selectedEvent && (
        <DeleteShortcut
          onClose={() => setShowDeleteShortcutModal(false)}
          shortCut={{
            id: selectedEvent.id,
            parentFolderId:
              selectedEvent.hierarchy && selectedEvent.hierarchy.length >= 2
                ? selectedEvent.hierarchy[selectedEvent.hierarchy.length - 2].id
                : selectedEvent.libraryFolderId,
            hierarchy: selectedEvent.hierarchy || [],
            isReference: referenceId ? true : false,
            type: referenceId ? "LibraryFolderReference" : "Gallery"
          }}
          onDeleteShortCut={onDeleteShortCut}
          type="event"
          referenceId={referenceId}
        />
      )}
      {showMovePopup && srcEntity && (
        <MoveToFolderPopup
          onClose={() => setShowMovePopup(false)}
          srcEntity={srcEntity}
          onMovingSuccess={val => onMovingSuccess(val)}
        />
      )}
    </>
  );
};

export default AlbumListBreadcrumb;
